// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { lazy, Suspense, Component } from 'react'
import type { ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { LanguageProvider } from './i18n/LanguageContext'
import Layout from './components/Layout'
import CookieBanner from './components/CookieBanner'

// Public pages — always in main bundle
import Landing from './pages/Landing'
import Login from './pages/Login'
import Signup from './pages/Signup'
import Founding from './pages/Founding'
import FoundingReview from './pages/FoundingReview'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Cookies from './pages/Cookies'
import DoNotSell from './pages/DoNotSell'
import Sitemap from './pages/Sitemap'
import Beta from './pages/Beta'
import Security from './pages/Security'
import DesignPartners from './pages/DesignPartners'

// All app pages — lazy loaded (separate chunks, only fetched on navigation)
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Logs = lazy(() => import('./pages/Logs'))
const Violations = lazy(() => import('./pages/Violations'))
const Policies = lazy(() => import('./pages/Policies'))
const PolicyBuilder = lazy(() => import('./pages/PolicyBuilder'))
const ComplianceHub = lazy(() => import('./pages/ComplianceHub'))
const RegulatoryIntelligence = lazy(() => import('./pages/RegulatoryIntelligence'))
const Settings = lazy(() => import('./pages/Settings'))
const Check = lazy(() => import('./pages/Check'))
const Docs = lazy(() => import('./pages/Docs'))
const Demo = lazy(() => import('./pages/Demo'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Playbook = lazy(() => import('./pages/Playbook'))
const Pitch = lazy(() => import('./pages/Pitch'))
const Agents = lazy(() => import('./pages/Agents'))
const Handoff = lazy(() => import('./pages/Handoff'))
const EmployeeBook = lazy(() => import('./pages/EmployeeBook'))
const InviteTeam = lazy(() => import('./pages/InviteTeam'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const PostBeta = lazy(() => import('./pages/PostBeta'))
const Wiki = lazy(() => import('./pages/Wiki'))
const WikiView = lazy(() => import('./pages/WikiView'))
const WikiEditor = lazy(() => import('./pages/WikiEditor'))
const Support = lazy(() => import('./pages/Support'))
const Compose = lazy(() => import('./pages/Compose'))
const Team = lazy(() => import('./pages/Team'))
const TeamProfile = lazy(() => import('./pages/TeamProfile'))
const Herald = lazy(() => import('./pages/Herald'))
const ContentStudio = lazy(() => import('./pages/ContentStudio'))
const EmployeeJoin = lazy(() => import('./pages/EmployeeJoin'))
const EmployeeInvite = lazy(() => import('./pages/EmployeeInvite'))
const ShieldSquad = lazy(() => import('./pages/ShieldSquad'))
const Chat = lazy(() => import('./pages/Chat'))
const IdentityEvents = lazy(() => import('./pages/IdentityEvents'))

// Vertical landing pages — public, no auth required
const FoundingAdmin = lazy(() => import('./pages/FoundingAdmin'))
const LandingHipaa = lazy(() => import('./pages/LandingHipaa'))
const LandingFinancial = lazy(() => import('./pages/LandingFinancial'))
const LandingLegal = lazy(() => import('./pages/LandingLegal'))
const LandingEnterprise = lazy(() => import('./pages/LandingEnterprise'))
const Newsletter = lazy(() => import('./pages/Newsletter'))
const About = lazy(() => import('./pages/About'))

// Interactive demo — public, no auth required
const LiveDemo = lazy(() => import('./pages/LiveDemo'))

// Extension magic invite link — public, no auth required
const InstallExtension = lazy(() => import('./pages/InstallExtension'))

// Redirects free-plan users to the upgrade page when they hit a paid route directly
function PaidRoute({ children }: { children: React.ReactNode }) {
  const { isPaid, isInternal, loading } = useAuth()
  if (loading) return null
  if (!isPaid && !isInternal) return <Navigate to="/settings?upgrade=1" replace />
  return <>{children}</>
}

// Blocks non-internal users from seeing internal team pages
function InternalRoute({ children }: { children: React.ReactNode }) {
  const { isInternal, loading } = useAuth()
  if (loading) return null
  if (!isInternal) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// Exec-only: internal + admin role — strategy docs, agent console, wiki, employee book
// Exec access: founders + co-founder only. guardrok.com = external contact only, never used for login.
const EXEC_EMAILS = ['dev@aiapas.com', 'gauri.verma@aiapas.com', 'ayuj.verma@aiapas.com']

function ExecRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (!EXEC_EMAILS.includes(user?.email ?? '')) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// Redirects users without the Policy Builder add-on to settings
function AddonRoute({ children }: { children: React.ReactNode }) {
  const { isPaid, isInternal, hasPolicyBuilder, loading } = useAuth()
  if (loading) return null
  if (!isPaid && !isInternal) return <Navigate to="/settings?upgrade=1" replace />
  if (!hasPolicyBuilder && !isInternal)
    return <Navigate to="/settings?addon=policy-builder" replace />
  return <>{children}</>
}

function ProtectedRoutes() {
  const { session, loading } = useAuth()
  if (loading)
    return (
      <div
        style={{ minHeight: '100vh', background: '#030712' }}
        className="flex items-center justify-center"
      >
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    )
  if (!session) return <Navigate to="/login" replace />
  return (
    <Layout>
      <Routes>
        {/* Free tier routes — always accessible */}
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/check" element={<Check />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/support" element={<Support />} />
        <Route
          path="/docs"
          element={
            <PaidRoute>
              <Docs />
            </PaidRoute>
          }
        />
        <Route
          path="/playbook"
          element={
            <PaidRoute>
              <Playbook />
            </PaidRoute>
          }
        />
        <Route
          path="/pitch"
          element={
            <PaidRoute>
              <Pitch />
            </PaidRoute>
          }
        />
        <Route
          path="/demo"
          element={
            <PaidRoute>
              <Demo />
            </PaidRoute>
          }
        />

        {/* Paid tier routes — redirect free users to upgrade */}
        <Route
          path="/logs"
          element={
            <PaidRoute>
              <Logs />
            </PaidRoute>
          }
        />
        <Route
          path="/violations"
          element={
            <PaidRoute>
              <Violations />
            </PaidRoute>
          }
        />
        <Route
          path="/policies"
          element={
            <PaidRoute>
              <Policies />
            </PaidRoute>
          }
        />
        <Route
          path="/policy-builder"
          element={
            <AddonRoute>
              <PolicyBuilder />
            </AddonRoute>
          }
        />
        <Route
          path="/compliance-hub"
          element={
            <PaidRoute>
              <ComplianceHub />
            </PaidRoute>
          }
        />
        <Route
          path="/regulatory-intelligence"
          element={
            <PaidRoute>
              <RegulatoryIntelligence />
            </PaidRoute>
          }
        />

        {/* Vault — customer team management, paid admins only */}
        <Route
          path="/vault"
          element={
            <PaidRoute>
              <EmployeeInvite />
            </PaidRoute>
          }
        />
        {/* GR-ID Identity Events — paid admins only */}
        <Route
          path="/identity-events"
          element={
            <PaidRoute>
              <IdentityEvents />
            </PaidRoute>
          }
        />

        {/* Internal team routes — is_internal=true only */}
        <Route
          path="/chat"
          element={
            <InternalRoute>
              <Chat />
            </InternalRoute>
          }
        />
        <Route
          path="/shield-squad"
          element={
            <InternalRoute>
              <ShieldSquad />
            </InternalRoute>
          }
        />
        <Route
          path="/invite"
          element={
            <InternalRoute>
              <InviteTeam />
            </InternalRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <InternalRoute>
              <Onboarding />
            </InternalRoute>
          }
        />

        {/* Exec-only routes — is_internal=true AND role=admin (strategy, agents, wiki, employee book) */}
        <Route
          path="/compose"
          element={
            <ExecRoute>
              <Compose />
            </ExecRoute>
          }
        />
        <Route
          path="/agents"
          element={
            <ExecRoute>
              <Agents />
            </ExecRoute>
          }
        />
        <Route
          path="/handoff"
          element={
            <ExecRoute>
              <Handoff />
            </ExecRoute>
          }
        />
        <Route
          path="/employee-book"
          element={
            <ExecRoute>
              <EmployeeBook />
            </ExecRoute>
          }
        />
        <Route
          path="/post-beta"
          element={
            <ExecRoute>
              <PostBeta />
            </ExecRoute>
          }
        />
        <Route
          path="/wiki"
          element={
            <ExecRoute>
              <Wiki />
            </ExecRoute>
          }
        />
        <Route
          path="/wiki/new"
          element={
            <ExecRoute>
              <WikiEditor />
            </ExecRoute>
          }
        />
        <Route
          path="/wiki/:slug/edit"
          element={
            <ExecRoute>
              <WikiEditor />
            </ExecRoute>
          }
        />
        <Route
          path="/wiki/:slug"
          element={
            <ExecRoute>
              <WikiView />
            </ExecRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ExecRoute>
              <Team />
            </ExecRoute>
          }
        />
        <Route
          path="/team/:id"
          element={
            <ExecRoute>
              <TeamProfile />
            </ExecRoute>
          }
        />
        <Route
          path="/herald"
          element={
            <ExecRoute>
              <Herald />
            </ExecRoute>
          }
        />
        <Route
          path="/content-studio"
          element={
            <ExecRoute>
              <ContentStudio />
            </ExecRoute>
          }
        />
        <Route
          path="/founding-admin"
          element={
            <ExecRoute>
              <FoundingAdmin />
            </ExecRoute>
          }
        />
      </Routes>
    </Layout>
  )
}

// / → Landing for guests, Dashboard for signed-in users
function HomeRoute() {
  const { session, loading } = useAuth()
  if (loading) return <PageLoader />
  if (session) return <Navigate to="/dashboard" replace />
  return <Landing />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { session, loading, isRecovery } = useAuth()
  if (loading) return null
  if (session && !isRecovery) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// Pricing requires signup — unauthenticated visitors go to /signup?next=/pricing
// Signed-in users see pricing normally (they can upgrade from there)
function PricingRoute() {
  const { session, loading } = useAuth()
  if (loading) return <PageLoader />
  if (!session) return <Navigate to="/signup?next=/pricing" replace />
  return <Pricing />
}

const PageLoader = () => (
  <div
    style={{ minHeight: '100vh', background: '#030712' }}
    className="flex items-center justify-center"
  >
    <div className="text-gray-500 text-sm">Loading...</div>
  </div>
)

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{ minHeight: '100vh', background: '#030712' }}
          className="flex items-center justify-center"
        >
          <div className="text-center">
            <div className="text-red-500 text-sm font-semibold mb-2">Something went wrong</div>
            <button
              onClick={() => {
                this.setState({ hasError: false })
                window.location.reload()
              }}
              className="text-xs text-gray-400 hover:text-gray-300 underline"
            >
              Reload page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <CookieBanner />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/landing" element={<Landing />} />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                {/* Signup is NOT wrapped in PublicRoute — logged-in users must reach
                    the card step (step 3) after account creation before being redirected */}
                <Route path="/signup" element={<Signup />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/do-not-sell" element={<DoNotSell />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/beta" element={<Beta />} />
                <Route path="/about" element={<About />} />
                <Route path="/security" element={<Security />} />
                <Route path="/design-partners" element={<DesignPartners />} />
                <Route path="/pricing" element={<PricingRoute />} />
                <Route path="/founding" element={<Founding />} />
                <Route path="/review" element={<FoundingReview />} />
                <Route path="/hipaa" element={<LandingHipaa />} />
                <Route path="/financial" element={<LandingFinancial />} />
                <Route path="/legal" element={<LandingLegal />} />
                <Route path="/enterprise" element={<LandingEnterprise />} />
                <Route path="/newsletter" element={<Newsletter />} />
                <Route path="/try" element={<LiveDemo />} />
                <Route path="/install" element={<InstallExtension />} />
                {/* Employee accept-invite — must be public (user arrives with a Supabase invite session) */}
                <Route path="/join" element={<EmployeeJoin />} />
                <Route path="/*" element={<ProtectedRoutes />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  )
}
